import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import EmptyState from "./EmptyState";
import TooltipUI from "./Tooltip";
import InfoIcon from "./InfoIcon";

const ClarityDistributionChart = ({ data }) => {
  // Define clarity level mappings
  const clarityLevels = {
    "80-100%": "Very clear",
    "60-79%": "Clear", 
    "40-59%": "Moderate",
    "20-39%": "Unclear",
    "0-19%": "Very unclear"
  };

  // Transform data to use meaningful labels
  const transformedData = data?.map(item => ({
    ...item,
    displayRange: clarityLevels[item.range] || item.range
  })) || [];

  // Calculate most distributed clarity level
  const getMostDistributedLevel = () => {
    if (!transformedData || transformedData.length === 0) return "No data";
    
    const maxItem = transformedData.reduce((max, current) => 
      current.count > max.count ? current : max
    );
    
    return maxItem.displayRange;
  };

  const mostDistributedLevel = getMostDistributedLevel();

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white-f p-3 border border-grey-20 rounded-md shadow-subtle">
          <p className="text-sm font-medium">{data.displayRange}</p>
          <p className="text-sm text-grey-60">
            <span className="font-medium">{data.percentage}%</span> ({data.count} messages)
          </p>
          <p className="text-xs text-grey-60">Score: {data.range}</p>
        </div>
      );
    }
    return null;
  };

  // Check if we have actual data
  if (!data || data.length === 0 || transformedData.length === 0) {
    return (
      <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle h-full">
        <div className="flex items-center gap-1 mb-4">
          <h3 className="text-sm font-normal text-black-0">Clarity distribution</h3>
          <TooltipUI
            content="How message clarity scores are distributed across buckets for the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <EmptyState
          variant="no-data"
          title="No clarity scores available"
          description="There's no clarity score data for the selected period. Try selecting a different date range."
          actionButton={null}
        />
      </div>
    );
  }

  return (
    <div className="bg-white-f p-5 rounded-lg border border-grey-20 shadow-subtle h-full flex flex-col">
      <div className="mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <h3 className="text-sm font-normal text-black-0">Clarity distribution</h3>
          <TooltipUI
            content="How message clarity scores are distributed across buckets for the selected period."
            placement="bottom"
            align="start"
          >
            <span className="inline-flex cursor-help">
              <InfoIcon />
            </span>
          </TooltipUI>
        </div>
        <p className="font-medium text-[18px] text-black-0">
          {mostDistributedLevel}
        </p>
      </div>
      
      <div className="flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={transformedData} 
            margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F5" />
            <XAxis 
              dataKey="displayRange" 
              stroke="#ABABBA"
              tick={{ fontSize: 12 }}
              tickMargin={10}
            />
            <YAxis 
              stroke="#ABABBA"
              tick={{ fontSize: 12 }}
              tickMargin={10}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar 
              dataKey="count" 
              fill="#31048B"
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default ClarityDistributionChart; 